import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Car, Gauge, Users, Info, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

/**
 * FleetCalculator — Live-Flotten-Rechner für Unternehmen.
 * Rechnet über POST /api/calculator/calculate (customer_type=business) —
 * damit identische Zahlen wie im Konfigurator (0,1 Punkte/km, 10%-Mitarbeiter-Regel, 49,90 €/Fzg).
 * Ab 201 Fahrzeugen: Rechner-Werte werden als Angebots-Anfrage abgesendet.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_VEHICLES = 200;
const MAX_INPUT = 9999;
const MAX_EMPLOYEES = 2000;

const fmtEUR = (v) =>
  v.toLocaleString('de-DE', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

export default function FleetCalculator({ bg = 'stone' }) {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState(20);
  const [vehiclesDraft, setVehiclesDraft] = useState('20');
  const [km, setKm] = useState(25000);
  const [employees, setEmployees] = useState(200);
  const [result, setResult] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const timer = useRef(null);

  const setVehiclesSafe = (n) => {
    setVehicles(n);
    setVehiclesDraft(n.toString());
    if (employees < n) setEmployees(n);
  };

  const commitVehicles = () => {
    const n = parseInt(vehiclesDraft.replace(/\D/g, ''), 10);
    const clamped = !n || isNaN(n) ? 20 : Math.max(1, Math.min(MAX_INPUT, n));
    setVehiclesSafe(clamped);
  };

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/calculator/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_type: 'business', vehicles, annual_km: km, employees }),
        });
        setResult(await res.json());
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [vehicles, km, employees]);

  const credit = result?.tank_credit ?? 0;
  const cost = result?.card_fee ?? 0;
  const factor = result?.roi ? Math.round(result.roi) : 0;
  const reduced = Boolean(result?.reduction_active);
  const enterprise = vehicles > MAX_VEHICLES;
  const fullVehicles = useMemo(() => Math.max(1, Math.floor(employees * 0.10)), [employees]);

  // Werte in den Konfigurator übernehmen (gleiche Seite: Event + Scroll, sonst /gold-card)
  const applyToConfigurator = () => {
    const payload = { vehicles, annualKm: km, employees };
    try { sessionStorage.setItem('fuelx_fleet_prefill', JSON.stringify(payload)); } catch (e) { /* noop */ }
    const el = document.querySelector('[data-testid="card-configurator"]');
    if (el) {
      window.dispatchEvent(new CustomEvent('fuelx-fleet-prefill', { detail: payload }));
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/gold-card');
    }
  };

  return (
    <section className={`py-24 sm:py-32 ${bg === 'white' ? 'bg-white' : 'bg-white'}`} data-testid="fleet-calculator">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-3.5 py-1.5 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-emerald-700">Flotten-Rechner · Live</span>
          </div>
          <h2 className="font-display text-stone-900 leading-[0.95] tracking-[-0.028em] text-[2.5rem] sm:text-[3.25rem] lg:text-[4rem] mb-5">
            Deine Flotte.<br />
            <span className="text-stone-400 italic">Dein</span> Jahres-Guthaben.
          </h2>
          <p className="text-lg text-stone-500 leading-relaxed">
            Stell Fahrzeugzahl, Kilometer und Mitarbeiterzahl ein — und sieh sofort, wie viel FuelGood Guthaben
            deine Flotte <strong className="text-stone-900">pro Jahr</strong> auflädt.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-0 rounded-[28px] overflow-hidden border border-stone-200 bg-white shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)]">
          {/* ═══ INPUTS ═══ */}
          <div className="p-8 sm:p-10 space-y-7 bg-white">
            {/* Fahrzeuge */}
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
                  <Car className="w-3.5 h-3.5" strokeWidth={2} />
                  Fahrzeuge in der Flotte
                </label>
                <span className="text-xs text-stone-400 tabular-nums">ab 201: individuelles Angebot</span>
              </div>
              <div className="relative mb-4">
                <input
                  type="text"
                  inputMode="numeric"
                  value={vehiclesDraft}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    setVehiclesDraft(raw);
                    const n = parseInt(raw, 10);
                    if (n >= 1) {
                      const c = Math.min(MAX_INPUT, n);
                      setVehicles(c);
                      if (employees < c) setEmployees(c);
                    }
                  }}
                  onBlur={commitVehicles}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className="w-full bg-white border border-stone-200 rounded-2xl px-5 py-4 pr-28 text-2xl font-medium text-stone-900 tabular-nums focus:outline-none focus:border-stone-900 transition-colors"
                  data-testid="fleet-vehicles-input"
                  aria-label="Anzahl Fahrzeuge"
                />
                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-medium pointer-events-none">Fahrzeuge</span>
              </div>
              <input
                type="range" min="1" max={MAX_VEHICLES} step="1" value={Math.min(vehicles, MAX_VEHICLES)}
                onChange={(e) => setVehiclesSafe(+e.target.value)}
                className="w-full accent-emerald-600"
                data-testid="fleet-vehicles-slider"
                aria-label="Anzahl Fahrzeuge (Slider)"
              />
              <div className="flex justify-between text-[10px] text-stone-400 mt-1 tabular-nums">
                <span>1</span><span>50</span><span>100</span><span>200</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {[5, 11, 20, 50, 100, 250].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVehiclesSafe(n)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors tabular-nums ${vehicles === n ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400 hover:text-stone-900'}`}
                    data-testid={`fleet-preset-${n}`}
                  >
                    {n} Fzg
                  </button>
                ))}
              </div>
            </div>

            {/* km pro Fahrzeug */}
            <div className="pt-2 border-t border-stone-100">
              <div className="flex justify-between mb-2">
                <label className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
                  <Gauge className="w-3.5 h-3.5" strokeWidth={2} />
                  Ø Kilometer pro Fahrzeug / Jahr
                </label>
                <span className="text-sm font-medium text-stone-900 tabular-nums" data-testid="fleet-km-value">{km.toLocaleString('de-DE')}&nbsp;km</span>
              </div>
              <input
                type="range" min="5000" max="50000" step="1000" value={km}
                onChange={(e) => setKm(+e.target.value)}
                className="w-full accent-emerald-600"
                data-testid="fleet-km-slider"
                aria-label="Durchschnittliche Kilometer pro Fahrzeug und Jahr"
              />
              <div className="flex justify-between text-[10px] text-stone-400 mt-1 tabular-nums">
                <span>5.000</span><span>25.000</span><span>50.000</span>
              </div>
            </div>

            {/* Mitarbeiter */}
            <div className="pt-2 border-t border-stone-100">
              <div className="flex justify-between mb-2">
                <label className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
                  <Users className="w-3.5 h-3.5" strokeWidth={2} />
                  Mitarbeiter im Unternehmen <span className="normal-case tracking-normal text-stone-400">(min. {vehicles})</span>
                </label>
                <span className="text-sm font-medium text-stone-900 tabular-nums" data-testid="fleet-employees-value">{employees.toLocaleString('de-DE')}</span>
              </div>
              <input
                type="range" min={Math.min(vehicles, MAX_EMPLOYEES)} max={Math.max(MAX_EMPLOYEES, vehicles)} step="1" value={Math.max(employees, vehicles)}
                onChange={(e) => setEmployees(+e.target.value)}
                className="w-full accent-emerald-600"
                data-testid="fleet-employees-slider"
                aria-label="Anzahl Mitarbeiter im Unternehmen"
              />
              <div className="flex justify-between text-[10px] text-stone-400 mt-1 tabular-nums">
                <span>{Math.min(vehicles, MAX_EMPLOYEES).toLocaleString('de-DE')}</span><span>{Math.max(1000, Math.round(vehicles / 2)).toLocaleString('de-DE')}</span><span>{Math.max(MAX_EMPLOYEES, vehicles).toLocaleString('de-DE')}</span>
              </div>
            </div>

            {/* 10%-Regel Status */}
            <div className={`rounded-2xl border p-4 flex items-start gap-3 transition-colors ${reduced ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="fleet-rate-hint">
              <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${reduced ? 'text-amber-600' : 'text-emerald-600'}`} strokeWidth={2} />
              <p className="text-xs leading-relaxed text-stone-600">
                {reduced ? (
                  <>10%-Regel: Bei {employees.toLocaleString('de-DE')} Mitarbeitern werden bis zu <strong className="text-stone-900">{fullVehicles.toLocaleString('de-DE')} Fahrzeuge</strong> voll angerechnet — dein Guthaben wird anteilig berechnet.</>
                ) : (
                  <>Volles Guthaben: Deine Flotte liegt innerhalb der <strong className="text-stone-900">10%-Regel</strong> (max. 10&nbsp;% der Belegschaft mit Dienstwagen).</>
                )}
              </p>
            </div>
          </div>

          {/* ═══ RESULT PANEL ═══ */}
          <div className="relative bg-stone-900 text-white p-8 sm:p-10 flex flex-col overflow-hidden" data-testid="fleet-result">
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />

            <div className="relative">
              <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-emerald-400 mb-3">FuelGood Guthaben deiner Flotte / Jahr</p>
              <div className="flex items-baseline gap-3 mb-1">
                <p className="text-5xl sm:text-6xl font-medium tracking-tight tabular-nums text-white" data-testid="fleet-credit-amount">
                  {Math.round(credit / 0.75).toLocaleString('de-DE')}
                </p>
                <span className="text-xl text-stone-400 font-light">Punkte</span>
              </div>
              <p className="text-sm text-emerald-300 mb-6 tabular-nums">
                {vehicles.toLocaleString('de-DE')} × {km.toLocaleString('de-DE')}&nbsp;km × 0,1&nbsp;Punkte{reduced ? ' · anteilig (10%-Regel)' : ''} · ≈ {fmtEUR(credit)}&nbsp;€ Gegenwert
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1.5">Jahreskosten</p>
                  <p className="text-2xl font-medium tabular-nums text-white whitespace-nowrap" data-testid="fleet-cost">{fmtEUR(cost)}&nbsp;€</p>
                  <p className="text-[10px] text-stone-500 mt-1 tabular-nums">{vehicles.toLocaleString('de-DE')} × 49,90&nbsp;€ / Jahr</p>
                </div>
                <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/20 p-5">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1.5">Wert-Faktor</p>
                  <p className="text-2xl font-medium tabular-nums text-emerald-300 whitespace-nowrap" data-testid="fleet-factor">{factor}×</p>
                  <p className="text-[10px] text-stone-500 mt-1">Guthaben vs. Jahresbeitrag</p>
                </div>
              </div>

              <ul className="space-y-2.5 mb-8">
                {[
                  'Guthaben sofort mit der Bestellung gutgeschrieben — kein Tracking, keine Telematik',
                  'Über 20 KFZ-Leistungen inklusive · Werkstatt, TÜV, Reifen, Versicherungs-Check',
                  'Alle Mitarbeiter buchen mit — dienstlich und privat',
                ].map((txt, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs text-stone-300">
                    <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    {txt}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mt-auto space-y-3">
              {enterprise ? (
                <button
                  type="button"
                  onClick={() => setQuoteOpen(true)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-emerald-400 text-stone-900 text-sm font-medium hover:bg-white transition-colors group"
                  data-testid="fleet-cta"
                >
                  <Building2 className="w-4 h-4" strokeWidth={2} />
                  Angebot für {vehicles.toLocaleString('de-DE')} Fahrzeuge anfordern
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={applyToConfigurator}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-stone-900 text-sm font-medium hover:bg-emerald-400 hover:text-white transition-colors group"
                  data-testid="fleet-cta"
                >
                  {vehicles} {vehicles === 1 ? 'Fahrzeug' : 'Fahrzeuge'} in den Konfigurator übernehmen
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </button>
              )}
              <p className="text-[10px] text-stone-500 text-center">
                {enterprise ? (
                  <>Deine Rechner-Werte werden direkt in die Anfrage übernommen — wir melden uns schnellstmöglich mit deinem Angebot</>
                ) : (
                  <>Fahrzeuge, Kilometer & Mitarbeiter werden vorausgefüllt · Ab 201 Fahrzeugen: <button type="button" onClick={() => setQuoteOpen(true)} className="underline text-stone-400 hover:text-white transition-colors" data-testid="fleet-enterprise-link">individuelles Angebot</button></>
                )}
              </p>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-stone-400 mt-5 max-w-3xl leading-relaxed">
          * Identische Berechnung wie im Konfigurator: 0,1&nbsp;Punkte FuelGood Guthaben pro km (1 Punkt = 0,75&nbsp;€), Gold Card 49,90&nbsp;€ pro Fahrzeug und Jahr.
          10%-Regel: Übersteigt die Zahl der Dienstwagen 10&nbsp;% der Belegschaft, wird das Guthaben anteilig berechnet.
          Einlösbar für Hotels (FuelGood Guthaben pro Buchung), Reifen- & Ölwechsel, TÜV und alle Auto-Services.
        </p>
      </div>

      <FleetQuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        vehicles={vehicles}
        km={km}
        employees={employees}
        credit={credit}
      />
    </section>
  );
}

/** Angebots-Anfrage-Dialog: übernimmt die Rechner-Werte und sendet sie ans FuelX-Team. */
function FleetQuoteDialog({ open, onOpenChange, vehicles, km, employees, credit }) {
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/service-inquiries/fleet-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicles,
          annual_km: km,
          employees,
          company: form.company,
          contact_name: form.name,
          contact_email: form.email,
          contact_phone: form.phone || null,
          message: form.message || null,
        }),
      });
      if (!res.ok) throw new Error('request failed');
      const data = await res.json();
      setReference(data.reference);
      toast.success('Anfrage gesendet — wir kümmern uns darum.');
    } catch (err) {
      toast.error('Anfrage konnte nicht gesendet werden. Bitte versuch es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'car-input';
  const labelCls = 'flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-medium mb-1.5';

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setReference(null); }}>
      <DialogContent className="sm:max-w-lg rounded-3xl neon-frame" data-testid="fleet-quote-dialog">
        {reference ? (
          <div className="py-6 text-center" data-testid="fleet-quote-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
              <Check className="w-6 h-6 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h3 className="font-display text-2xl text-stone-900 mb-2">Anfrage eingegangen.</h3>
            <p className="text-sm text-stone-500 mb-5 leading-relaxed">
              Dein persönlicher Ansprechpartner meldet sich schnellstmöglich mit individuellen Konditionen für {vehicles.toLocaleString('de-DE')} Fahrzeuge.
            </p>
            <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 inline-block mb-6">
              <p className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-0.5">Referenz</p>
              <p className="text-base font-mono text-stone-900 tracking-wider" data-testid="fleet-quote-reference">{reference}</p>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-full px-6 py-3.5 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-stone-700 transition-colors"
              data-testid="fleet-quote-close"
            >
              Schließen
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-stone-900">Individuelles Flotten-Angebot</DialogTitle>
              <DialogDescription className="text-sm text-stone-500">
                Deine Rechner-Werte werden direkt übernommen — dein persönlicher Ansprechpartner meldet sich schnellstmöglich mit deinem Angebot.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2 mb-2" data-testid="fleet-quote-summary">
              {[
                [vehicles.toLocaleString('de-DE'), 'Fahrzeuge'],
                [`${km.toLocaleString('de-DE')} km`, 'Ø / Fzg / Jahr'],
                [employees.toLocaleString('de-DE'), 'Mitarbeiter'],
              ].map(([v, l]) => (
                <div key={l} className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 text-center">
                  <p className="text-sm font-medium text-stone-900 tabular-nums">{v}</p>
                  <p className="text-[10px] text-stone-400">{l}</p>
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>Firma <span className="text-amber-600 ml-0.5">*</span></span>
                  <input required minLength={2} placeholder="z. B. Muster GmbH" value={form.company} onChange={set('company')} className={inputCls} data-testid="fleet-quote-company" />
                </label>
                <label className="block">
                  <span className={labelCls}>Ansprechpartner <span className="text-amber-600 ml-0.5">*</span></span>
                  <input required minLength={2} placeholder="Vor- und Nachname" value={form.name} onChange={set('name')} className={inputCls} data-testid="fleet-quote-name" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>E-Mail <span className="text-amber-600 ml-0.5">*</span></span>
                  <input required type="email" placeholder="du@firma.de" value={form.email} onChange={set('email')} className={inputCls} data-testid="fleet-quote-email" />
                </label>
                <label className="block">
                  <span className={labelCls}>Telefon (optional)</span>
                  <input placeholder="+49 …" value={form.phone} onChange={set('phone')} className={inputCls} data-testid="fleet-quote-phone" />
                </label>
              </div>
              <label className="block">
                <span className={labelCls}>Nachricht (optional)</span>
                <textarea rows={3} placeholder="z. B. Fahrzeugtypen, gewünschter Start" value={form.message} onChange={set('message')} className={`${inputCls} resize-none`} data-testid="fleet-quote-message" />
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-all shadow-md disabled:opacity-60"
                data-testid="fleet-quote-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : null}
                Angebot anfordern
              </button>
              <p className="text-[10px] text-stone-400 text-center leading-relaxed">
                Unverbindlich · Wir melden uns schnellstmöglich · Deine Daten werden nur zur Angebotserstellung genutzt
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
